import React, { useState, useEffect } from 'react';
import { monitorReportAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MonitorReport = () => {
  const { tenant, user } = useAuth();
  const primaryColor = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';
  const monitorRoles = ["network_monitor", "dist_l1", "dist_l2", "dist_l3"];
  const isViewer = user && (!monitorRoles.includes(user.role) || user.role === 'isp_dist_head' || user.role === 'director');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [editingReport, setEditingReport] = useState(null);
  const [activeTab, setActiveTab] = useState(isViewer ? 'view' : 'submit');
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const [filter, setFilter] = useState({ date_from: '', date_to: today, shift: '' });
  const [showFilter, setShowFilter] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    shift: 'morning',
    overall_status: 'normal',
    monitoring_tool: 'PRTG',
    devices_monitored: '',
    devices_with_issues: '',
    incidents: { occurred: false, details: '', actions_taken: '', resolved: 'na', pending_issues: '' },
    fiber_path: { status: 'normal', details: '' },
    mac_flapping: { status: 'none', details: '' },
    log_buffer: { status: 'none', details: '' },
    trap_buffer: { status: 'none', details: '' },
    port_errors: { status: 'none', details: '' },
    remarks: '',
    recommendations: ''
  });

  useEffect(() => {
    if (isViewer) loadReports({ date_from: yesterday, date_to: today, shift: '' });
    else loadMyReports();
  }, []);

  const loadReports = async (f) => {
    setLoading(true);
    try {
      const res = await monitorReportAPI.getAll(f || filter);
      setReports(res.data.reports || []);
    } catch (err) { setError('Failed to load reports'); }
    finally { setLoading(false); }
  };

  const loadMyReports = async () => {
    setLoading(true);
    try {
      const res = await monitorReportAPI.getAll({});
      setReports(res.data.reports || []);
    } catch (err) {}
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await monitorReportAPI.submit(form);
      setSuccess('Report submitted successfully!');
      setForm({ ...form, incidents: { occurred: false, details: '', actions_taken: '', resolved: 'na', pending_issues: '' }, fiber_path: { status: 'normal', details: '' }, mac_flapping: { status: 'none', details: '' }, log_buffer: { status: 'none', details: '' }, trap_buffer: { status: 'none', details: '' }, port_errors: { status: 'none', details: '' }, remarks: '', recommendations: '', devices_monitored: '', devices_with_issues: '' });
      loadMyReports();
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await monitorReportAPI.update(editingReport._id, form);
      setSuccess('Report updated successfully!');
      setEditingReport(null);
      loadMyReports();
      setActiveTab('view');
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to update');
    } finally { setSubmitting(false); }
  };

  const startEdit = (r) => {
    setEditingReport(r);
    setForm({
      date: new Date(r.date).toISOString().split('T')[0],
      shift: r.shift,
      overall_status: r.overall_status,
      monitoring_tool: r.monitoring_tool || 'PRTG',
      devices_monitored: r.devices_monitored || '',
      devices_with_issues: r.devices_with_issues || '',
      incidents: r.incidents || { occurred: false, details: '', actions_taken: '', resolved: 'na', pending_issues: '' },
      fiber_path: r.fiber_path || { status: 'normal', details: '' },
      mac_flapping: r.mac_flapping || { status: 'none', details: '' },
      log_buffer: r.log_buffer || { status: 'none', details: '' },
      trap_buffer: r.trap_buffer || { status: 'none', details: '' },
      port_errors: r.port_errors || { status: 'none', details: '' },
      remarks: r.remarks || '',
      recommendations: r.recommendations || ''
    });
    setActiveTab('submit');
  };

  const handleDownload = () => {
    if (reports.length === 0) return;
    const rows = [
      ['Network Monitor Reports', tenant ? tenant.branding.app_name : ''],
      [''],
      ['Date', 'Shift', 'Submitted By', 'Overall Status', 'Devices Monitored', 'Issues', 'Incidents', 'Fiber Path', 'MAC Flapping', 'Log Buffer', 'Trap Buffer', 'Port Errors', 'Actions Taken', 'Remarks'],
      ...reports.map(r => [
        new Date(r.date).toLocaleDateString(),
        r.shift, r.submitted_by ? r.submitted_by.name : '',
        r.overall_status, r.devices_monitored, r.devices_with_issues,
        r.incidents.occurred ? r.incidents.details : 'None',
        r.fiber_path.status + (r.fiber_path.details ? ': ' + r.fiber_path.details : ''),
        r.mac_flapping.status + (r.mac_flapping.details ? ': ' + r.mac_flapping.details : ''),
        r.log_buffer.status + (r.log_buffer.details ? ': ' + r.log_buffer.details : ''),
        r.trap_buffer.status + (r.trap_buffer.details ? ': ' + r.trap_buffer.details : ''),
        r.port_errors.status + (r.port_errors.details ? ': ' + r.port_errors.details : ''),
        r.incidents.actions_taken || '',
        r.remarks || ''
      ])
    ];
    const csv = rows.map(r => r.map(c => '"' + String(c || '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'MonitorReports_' + new Date().toISOString().split('T')[0] + '.csv';
    a.click();
  };

  const statusColor = { normal: '#4caf50', warning: '#ff9800', critical: '#f44336', none: '#4caf50', detected: '#f44336', found: '#f44336', issues: '#f44336' };
  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#444' };
  const sectionStyle = { background: '#f9f9f9', borderRadius: '8px', padding: '16px', marginBottom: '16px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px', color: '#333' }}>📡 Network Monitor Reports</h2>
          <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>Daily network monitoring shift reports</p>
        </div>
        {isViewer && <button onClick={handleDownload} disabled={reports.length === 0} style={{ padding: '10px 16px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>⬇️ Download CSV</button>}
      </div>

      <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
          {!isViewer && <button onClick={() => setActiveTab('submit')} style={{ flex: 1, padding: '14px', border: 'none', borderBottom: activeTab === 'submit' ? '3px solid ' + primaryColor : '3px solid transparent', background: 'transparent', color: activeTab === 'submit' ? primaryColor : '#666', fontWeight: activeTab === 'submit' ? '700' : '400', cursor: 'pointer', fontSize: '15px' }}>📝 Submit Report</button>}
          <button onClick={() => { setActiveTab('view'); loadReports(); }} style={{ flex: 1, padding: '14px', border: 'none', borderBottom: activeTab === 'view' ? '3px solid ' + primaryColor : '3px solid transparent', background: 'transparent', color: activeTab === 'view' ? primaryColor : '#666', fontWeight: activeTab === 'view' ? '700' : '400', cursor: 'pointer', fontSize: '15px' }}>{isViewer ? '📊 All Reports' : '📋 My Reports'}</button>
        </div>
      </div>

      {success && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontWeight: '600' }}>{success}</div>}
      {error && <div style={{ background: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>{error}</div>}

      {activeTab === 'submit' && !isViewer && (
        <div style={{ background: 'white', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <form onSubmit={editingReport ? handleEdit : handleSubmit}>
            <div style={sectionStyle}>
              <h4 style={{ margin: '0 0 16px', color: primaryColor }}>📋 Basic Information</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div><label style={labelStyle}>Date *</label><input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required style={inputStyle} /></div>
                <div><label style={labelStyle}>Shift *</label>
                  <select value={form.shift} onChange={e => setForm({...form, shift: e.target.value})} style={inputStyle}>
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="evening">Evening</option>
                    <option value="night">Night</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Overall Status *</label>
                  <select value={form.overall_status} onChange={e => setForm({...form, overall_status: e.target.value})} style={{ ...inputStyle, color: statusColor[form.overall_status], fontWeight: '700' }}>
                    <option value="normal">✅ Normal</option>
                    <option value="warning">⚠️ Warning</option>
                    <option value="critical">🔴 Critical</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div><label style={labelStyle}>Monitoring Tool</label><input type="text" value={form.monitoring_tool} onChange={e => setForm({...form, monitoring_tool: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Devices Monitored</label><input type="number" value={form.devices_monitored} onChange={e => setForm({...form, devices_monitored: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Devices with Issues</label><input type="number" value={form.devices_with_issues} onChange={e => setForm({...form, devices_with_issues: e.target.value})} style={inputStyle} /></div>
              </div>
            </div>

            <div style={sectionStyle}>
              <h4 style={{ margin: '0 0 16px', color: primaryColor }}>🔍 Network Checks</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Fiber Path Status</label>
                  <select value={form.fiber_path.status} onChange={e => setForm({...form, fiber_path: {...form.fiber_path, status: e.target.value}})} style={inputStyle}>
                    <option value="normal">✅ Normal</option>
                    <option value="issues">⚠️ Issues Found</option>
                  </select>
                  {form.fiber_path.status === 'issues' && <textarea placeholder="Describe fiber path issues..." value={form.fiber_path.details} onChange={e => setForm({...form, fiber_path: {...form.fiber_path, details: e.target.value}})} rows={2} style={{...inputStyle, marginTop: '8px', resize: 'vertical'}} />}
                </div>
                <div>
                  <label style={labelStyle}>MAC Flapping</label>
                  <select value={form.mac_flapping.status} onChange={e => setForm({...form, mac_flapping: {...form.mac_flapping, status: e.target.value}})} style={inputStyle}>
                    <option value="none">✅ None</option>
                    <option value="detected">⚠️ Detected</option>
                  </select>
                  {form.mac_flapping.status === 'detected' && <textarea placeholder="Describe MAC flapping details..." value={form.mac_flapping.details} onChange={e => setForm({...form, mac_flapping: {...form.mac_flapping, details: e.target.value}})} rows={2} style={{...inputStyle, marginTop: '8px', resize: 'vertical'}} />}
                </div>
                <div>
                  <label style={labelStyle}>Log Buffer Alerts</label>
                  <select value={form.log_buffer.status} onChange={e => setForm({...form, log_buffer: {...form.log_buffer, status: e.target.value}})} style={inputStyle}>
                    <option value="none">✅ None</option>
                    <option value="warning">⚠️ Warning</option>
                    <option value="critical">🔴 Critical</option>
                  </select>
                  {form.log_buffer.status !== 'none' && <textarea placeholder="Describe log buffer alerts..." value={form.log_buffer.details} onChange={e => setForm({...form, log_buffer: {...form.log_buffer, details: e.target.value}})} rows={2} style={{...inputStyle, marginTop: '8px', resize: 'vertical'}} />}
                </div>
                <div>
                  <label style={labelStyle}>Trap Buffer Alerts</label>
                  <select value={form.trap_buffer.status} onChange={e => setForm({...form, trap_buffer: {...form.trap_buffer, status: e.target.value}})} style={inputStyle}>
                    <option value="none">✅ None</option>
                    <option value="warning">⚠️ Warning</option>
                    <option value="critical">🔴 Critical</option>
                  </select>
                  {form.trap_buffer.status !== 'none' && <textarea placeholder="Describe trap buffer alerts..." value={form.trap_buffer.details} onChange={e => setForm({...form, trap_buffer: {...form.trap_buffer, details: e.target.value}})} rows={2} style={{...inputStyle, marginTop: '8px', resize: 'vertical'}} />}
                </div>
                <div>
                  <label style={labelStyle}>Port Errors</label>
                  <select value={form.port_errors.status} onChange={e => setForm({...form, port_errors: {...form.port_errors, status: e.target.value}})} style={inputStyle}>
                    <option value="none">✅ None</option>
                    <option value="found">⚠️ Found</option>
                  </select>
                  {form.port_errors.status === 'found' && <textarea placeholder="Describe port errors..." value={form.port_errors.details} onChange={e => setForm({...form, port_errors: {...form.port_errors, details: e.target.value}})} rows={2} style={{...inputStyle, marginTop: '8px', resize: 'vertical'}} />}
                </div>
              </div>
            </div>

            <div style={sectionStyle}>
              <h4 style={{ margin: '0 0 16px', color: primaryColor }}>🚨 Incidents</h4>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.incidents.occurred} onChange={e => setForm({...form, incidents: {...form.incidents, occurred: e.target.checked}})} style={{ width: '18px', height: '18px' }} />
                  <span style={{ fontWeight: '600', color: '#333' }}>Any incidents occurred during this shift?</span>
                </label>
              </div>
              {form.incidents.occurred && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div><label style={labelStyle}>Incident Details *</label><textarea placeholder="Describe what happened..." value={form.incidents.details} onChange={e => setForm({...form, incidents: {...form.incidents, details: e.target.value}})} rows={3} style={{...inputStyle, resize: 'vertical'}} /></div>
                  <div><label style={labelStyle}>Actions Taken</label><textarea placeholder="What actions were taken..." value={form.incidents.actions_taken} onChange={e => setForm({...form, incidents: {...form.incidents, actions_taken: e.target.value}})} rows={3} style={{...inputStyle, resize: 'vertical'}} /></div>
                  <div>
                    <label style={labelStyle}>Resolution Status</label>
                    <select value={form.incidents.resolved} onChange={e => setForm({...form, incidents: {...form.incidents, resolved: e.target.value}})} style={inputStyle}>
                      <option value="yes">✅ Fully Resolved</option>
                      <option value="partial">⚠️ Partially Resolved</option>
                      <option value="no">🔴 Not Resolved</option>
                    </select>
                  </div>
                  <div><label style={labelStyle}>Pending Issues</label><textarea placeholder="Any pending issues..." value={form.incidents.pending_issues} onChange={e => setForm({...form, incidents: {...form.incidents, pending_issues: e.target.value}})} rows={3} style={{...inputStyle, resize: 'vertical'}} /></div>
                </div>
              )}
            </div>

            <div style={sectionStyle}>
              <h4 style={{ margin: '0 0 16px', color: primaryColor }}>📝 Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={labelStyle}>Overall Remarks</label><textarea placeholder="General observations and remarks..." value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} rows={3} style={{...inputStyle, resize: 'vertical'}} /></div>
                <div><label style={labelStyle}>Recommendations</label><textarea placeholder="Any recommendations for improvement..." value={form.recommendations} onChange={e => setForm({...form, recommendations: e.target.value})} rows={3} style={{...inputStyle, resize: 'vertical'}} /></div>
              </div>
            </div>

            <button type="submit" disabled={submitting} style={{ width: '100%', padding: '14px', background: submitting ? '#ccc' : primaryColor, color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer' }}>
              {submitting ? 'Saving...' : editingReport ? '✏️ Update Report' : '📤 Submit Monitor Report'}
            </button>
            {editingReport && <button type='button' onClick={() => { setEditingReport(null); setActiveTab('view'); }} style={{ width: '100%', padding: '12px', background: 'white', color: '#333', border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px', cursor: 'pointer', marginTop: '8px' }}>Cancel Edit</button>}
          </form>
        </div>
      )}

      {activeTab === 'view' && (
        <div>
          {isViewer && (
            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showFilter ? '16px' : '0' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>
                  📅 Showing: <strong>{filter.date_from || 'All'}</strong> to <strong>{filter.date_to || 'All'}</strong>
                  {filter.shift && <> | Shift: <strong>{filter.shift}</strong></>}
                  <span style={{ marginLeft: '8px', color: '#999' }}>({reports.length} reports)</span>
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => { const f = { date_from: yesterday, date_to: today, shift: '' }; setFilter(f); loadReports(f); }} style={{ padding: '6px 12px', background: '#e3f2fd', color: '#1565c0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Today & Yesterday</button>
                  <button onClick={() => setShowFilter(!showFilter)} style={{ padding: '6px 12px', background: primaryColor + '20', color: primaryColor, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>🔍 {showFilter ? 'Hide Filter' : 'Filter'}</button>
                </div>
              </div>
              {showFilter && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '12px', alignItems: 'flex-end', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                  <div><label style={labelStyle}>From Date</label><input type="date" value={filter.date_from} onChange={e => setFilter({...filter, date_from: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>To Date</label><input type="date" value={filter.date_to} onChange={e => setFilter({...filter, date_to: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Shift</label>
                    <select value={filter.shift} onChange={e => setFilter({...filter, shift: e.target.value})} style={inputStyle}>
                      <option value="">All Shifts</option>
                      <option value="morning">Morning</option>
                      <option value="afternoon">Afternoon</option>
                      <option value="evening">Evening</option>
                      <option value="night">Night</option>
                    </select>
                  </div>
                  <button onClick={() => { loadReports(filter); setShowFilter(false); }} style={{ padding: '10px 20px', background: primaryColor, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>🔍 Apply</button>
                </div>
              )}
            </div>
          )}

          {loading ? <p style={{ textAlign: 'center', padding: '20px' }}>Loading...</p> : reports.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '10px', padding: '40px', textAlign: 'center' }}>
              <p style={{ color: '#999' }}>No reports found</p>
            </div>
          ) : reports.map(r => (
            <div key={r._id} style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '16px', borderLeft: '4px solid ' + (statusColor[r.overall_status] || '#4caf50') }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px', color: '#333' }}>{new Date(r.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h3>
                  <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                    🕐 {r.shift.charAt(0).toUpperCase() + r.shift.slice(1)} Shift
                    {r.submitted_by && ' • 👤 ' + r.submitted_by.name}
                    {' • 🛠️ ' + (r.monitoring_tool || 'PRTG')}
                  </p>
                </div>
                <span style={{ background: (statusColor[r.overall_status] || '#4caf50') + '20', color: statusColor[r.overall_status] || '#4caf50', padding: '6px 14px', borderRadius: '20px', fontWeight: '700', fontSize: '13px' }}>
                  {r.overall_status.toUpperCase()}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'Fiber Path', value: r.fiber_path.status, details: r.fiber_path.details },
                  { label: 'MAC Flapping', value: r.mac_flapping.status, details: r.mac_flapping.details },
                  { label: 'Log Buffer', value: r.log_buffer.status, details: r.log_buffer.details },
                  { label: 'Trap Buffer', value: r.trap_buffer.status, details: r.trap_buffer.details },
                  { label: 'Port Errors', value: r.port_errors.status, details: r.port_errors.details }
                ].map(item => (
                  <div key={item.label} style={{ background: (statusColor[item.value] || '#4caf50') + '15', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid ' + (statusColor[item.value] || '#4caf50') + '30' }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{item.label}</div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: statusColor[item.value] || '#4caf50' }}>{item.value.toUpperCase()}</div>
                  </div>
                ))}
              </div>
              {r.incidents.occurred && (
                <div style={{ background: '#fff3e0', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                  <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#e65100', fontSize: '13px' }}>🚨 Incident Reported</p>
                  <p style={{ margin: '0 0 4px', fontSize: '13px' }}>{r.incidents.details}</p>
                  {r.incidents.actions_taken && <p style={{ margin: '0', fontSize: '13px', color: '#555' }}>Actions: {r.incidents.actions_taken}</p>}
                </div>
              )}
              {r.remarks && <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#555' }}>📝 {r.remarks}</p>}
              {monitorRoles.includes(user.role) && new Date(r.date).toDateString() === new Date().toDateString() && r.submitted_by && (r.submitted_by._id === user.id || r.submitted_by._id === user._id) && (
                <button onClick={() => startEdit(r)} style={{ padding: '6px 14px', background: '#ff980020', color: '#e65100', border: '1px solid #ff9800', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', marginTop: '8px' }}>✏️ Edit Report</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MonitorReport;